import React, { useState, useEffect, useRef } from "react";
import { Application, BlankApplication, Status } from "../../types";
import { supabase } from "../../utils/supabase";
import ApprovalEmail from "../../templates/ApprovalEmail";
import toast from "react-hot-toast";
import RejectionEmail from "../../templates/RejectionEmail";

interface EditModalProps {
  Application: Application | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (Application: Application) => void;
}

const EditModal: React.FC<EditModalProps> = ({
  Application,
  isOpen,
  onClose,
  onSave,
}) => {
  const [form, setForm] =
    useState<Omit<Application, "id" | "registrationDate">>(BlankApplication);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (Application) {
      setForm({
        studentId: Application.studentId,
        studentEmail: Application.studentEmail,
        fullName: Application.fullName,
        businessName: Application.businessName,
        status: Application.status,
        observations: Application.observations || "",
        businessLogo: Application.businessLogo || "",
        studentFaculty: Application.studentFaculty || "",
        businessCategory: Application.businessCategory || "",
        businessURL: Application.businessURL,
      });
    }
  }, [Application]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (Application) {
      onSave({
        ...Application,
        ...form,
      });
      if (form.status === Status.Approved) {
        try {
          toast.loading("Generando documentos...");

          // Generar carta de aprobación
          const approvalResponse = await fetch(
            "https://uatformsapi.onrender.com/generate-approval",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                fullName: form.fullName.trim(),
                businessName: form.businessName.trim(),
              }),
            }
          );

          if (!approvalResponse.ok) {
            throw new Error(
              `Error al generar la carta: ${approvalResponse.statusText}`
            );
          }

          const approvalData = await approvalResponse.json();
          const approvalURL = approvalData.base64;

          // Generar póster
          const posterResponse = await fetch(
            "https://uatformsapi.onrender.com/generate-poster",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                fullName: form.fullName.trim(),
                businessName: form.businessName.trim(),
                businessURL: form.businessURL.trim(),
                businessLogo: form.businessLogo.trim(),
              }),
            }
          );

          if (!posterResponse.ok) {
            throw new Error(
              `Error al generar el póster: ${posterResponse.statusText}`
            );
          }

          const posterData = await posterResponse.json();
          const posterURL = posterData.base64;

          toast.dismiss(); // cierra el toast de carga
          toast.loading("Enviando correo...");

          // Enviar correo solo si ambas peticiones fueron exitosas
          const email = {
            from: "Feria UAT <support@feriadeemprendedoresuat.site>",
            to: form.studentEmail,
            subject: "Resultado de tu solicitud",
            html: ApprovalEmail({
              fullName: form.fullName,
              businessName: form.businessName,
            }),
            attachments: [
              {
                content: approvalURL,
                filename: `Carta-${form.fullName}.pdf`,
              },
              {
                content: posterURL,
                filename: `Poster-${form.fullName}.pdf`,
              },
            ],
          };

          const { error } = await supabase.functions.invoke("resend", {
            body: { email },
          });

          toast.dismiss();

          if (error) {
            throw new Error(`Error al enviar correo: ${error.message}`);
          }

          toast.success("Correo enviado correctamente 🎉");
        } catch (error) {
          const err = error as unknown as Error;
          toast.dismiss();
          toast.error(`❌ ${err.message}`);
          console.error("Error en el proceso:", err);
        }
      }
    }
    if (form.status === Status.Rejected) {
      const email = {
        from: "Feria UAT <support@feriadeemprendedoresuat.site>",
        to: form.studentEmail,
        subject: "Resultado de tu solicitud",
        html: RejectionEmail({
          fullName: form.fullName,
          observations: form.observations,
        }),
      };

      toast.loading("Enviando correo...");

      const { error } = await supabase.functions.invoke("rejected", {
        body: { email },
      });

      toast.dismiss();

      if (error) {
        throw new Error(`Error al enviar correo: ${error.message}`);
      }

      toast.success("Correo enviado correctamente 🎉");
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <h3 className="text-xl font-semibold text-[var(--uat-blue)] mb-4">
            Editar Registro
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Matrícula
              </label>
              <input
                type="text"
                value={form.studentId}
                onChange={(e) =>
                  setForm({ ...form, studentId: e.target.value })
                }
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--uat-accent)]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre Completo
              </label>
              <input
                type="text"
                value={form.fullName}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--uat-accent)]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre del Emprendimiento
              </label>
              <input
                type="text"
                value={form.businessName}
                onChange={(e) =>
                  setForm({ ...form, businessName: e.target.value })
                }
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--uat-accent)]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado
              </label>
              <select
                value={form.status}
                onChange={(e) =>
                  setForm({
                    ...form,
                    status: e.target.value as Status,
                  })
                }
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--uat-accent)]"
              >
                <option value="pending">Pendiente</option>
                <option value="approved">Aprobado</option>
                <option value="rejected">Rechazado</option>
              </select>
            </div>

            {form.status === Status.Rejected && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observaciones
                </label>
                <textarea
                  value={form.observations}
                  onChange={(e) =>
                    setForm({ ...form, observations: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--uat-accent)] min-h-[100px]"
                  placeholder="Ingrese las razones del rechazo..."
                  required={form.status === "rejected"}
                />
              </div>
            )}

            {/* {form.status === Status.Approved && (
              <div>
                <PDFDownloadLink
                  document={
                    <ApprovalLetter
                      fullName={form.fullName}
                      projectTitle={form.businessName}
                      eventName="Feria de Emprendedores"
                      eventDates="27 de Noviembre de 2025"
                    />
                  }
                >
                  "Download letter!"
                </PDFDownloadLink>
                <PDFDownloadLink
                  document={
                    <StandPosterDocument
                      businessLogo={form.businessLogo}
                      businessName={form.businessName}
                      businessURL={form.businessURL}
                      fullName={form.fullName}
                    />
                  }
                >
                  "Download poster!"
                </PDFDownloadLink>
              </div>
            )} */}

            <input
              type="file"
              name="document"
              className="hidden"
              ref={inputRef}
            />

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-[var(--uat-accent)] hover:bg-[var(--uat-accent)]/90 rounded-md"
              >
                Guardar Cambios
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditModal;
